// @ts-strict-ignore
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useDeviceStream } from '@managexr/react-streaming';

import { useAppSelector, useAppDispatch } from '../../redux';
import { PageContainer } from '../../components/pageContainer';
import { Button } from '../../components/button';
import { Text } from '../../components/text';
import { primary } from '../../utils/colors';
import { Spinner } from '../../components/Spinner';
import { formatName } from '../../utils/user';
import { getManageXRToken } from '../../redux/actions/admin';
import { get as getClass } from '../../redux/actions/class';

export const ScreencastPage: React.FC = () => {
  const query = new URLSearchParams(window.location.search);
  const classId = parseInt(query.get('class-id'));
  const studentId = parseInt(query.get('student-id'));
  const serialCode = query.get('serial-code');
  const hardwareType = query.get('hardware-type');
  const debug = query.get('debug') ? true : false;

  if (debug) console.log(`>>> start streaming page render`);

  const fetchingClass = useAppSelector((state) => state.class.fetchingClass);
  const thisClass = useAppSelector((state) => state.class.classes.find((c) => c.id === classId));
  const fetchingManageXRToken = useAppSelector((state) => state.admin.fetchingManageXRToken);
  const manageXRTokens = useAppSelector((state) => state.admin.manageXRTokens, {});
  const hasError = useAppSelector((state) => state.admin.hasError);
  const errorMessage = useAppSelector((state) => state.admin.errorMessage);

  const [studentName, setStudentName] = useState<string>('');

  const dispatch = useAppDispatch();

  const token = studentId in manageXRTokens ? manageXRTokens[studentId] : null;

  useEffect(() => {
    if (classId && !thisClass && !fetchingClass && !hasError) {
      dispatch(getClass(classId));
    }
  }, []);

  useEffect(() => {
    if (thisClass && thisClass.students.some((s) => s.id == studentId)) {
      const name = formatName(thisClass.students.find((s) => s.id == studentId));
      setStudentName(name);
      document.title = name;
    }
  }, [thisClass]);

  useEffect(() => {
    if (debug) {
      console.log(`>>>> starting with serialCode=${serialCode} studentId=${studentId} on ${hardwareType}`);
    }
    if (!hasError) {
      if (!fetchingManageXRToken && !(studentId in manageXRTokens)) {
        if (debug) console.log('>>>> start dispatch');
        dispatch(getManageXRToken({ serialCode, studentId }));
      }
    }
  }, [fetchingManageXRToken]);

  const studentNameComponent = (
    <Text center variant="md" color={primary}>
      {studentName}
    </Text>
  );

  return (
    <PageContainer header={studentNameComponent} leftNav={null}>
      <Content>
        {hasError ? (
          <Text variant="md">Error:&nbsp; {errorMessage}</Text>
        ) : fetchingManageXRToken || !(studentId in manageXRTokens) ? (
          <>
            <Spinner />
            <Text variant="md">Authenticating with ManageXR</Text>
          </>
        ) : (
          <StreamingManageXR deviceId={serialCode} token={token} debug={debug} />
        )}
      </Content>
    </PageContainer>
  );
};

// Copied from https://docs.managexr.com/streaming-sdk-reference/usage#devicestreamstatus
const statusDescription = {
  pending_online: 'Device is offline; will connect once online.',
  pending_permission: 'Waiting for in-headset screen capture permission.',
  pending_connection: 'Connecting after permission granted.',
  connected: 'Stream is live and visible.',
  reconnecting: 'Network disconnection; attempting reconnect.',
  error_request_failed: 'Device did not respond to start request.',
  error_permission_dismissed: 'User dismissed screen capture prompt.',
  error_permission_denied: 'User denied screen capture permission.',
  error_failed_to_start: 'Stream failed despite granted permission.',
  error_terminated_on_device: 'Stream ended by device (e.g., sleep mode).',
  error_connection: 'Network issue ended an active stream.',
  error_already_streaming: 'Device is already streaming to another client.',
};
const waitingStatus = ['pending_online', 'pending_permission', 'pending_connection', 'reconnected'];

const StreamingManageXR = ({ deviceId, token, debug }: { deviceId: string; token: string; debug: boolean }) => {
  const { endOtherStream, status, videoRef } = useDeviceStream(deviceId, token);
  if (debug) console.log(`>>>>> call to StreamingManageXR status=${status}`);

  const statusText = status in statusDescription ? statusDescription[status] : 'Unknown error';
  const isWaiting = waitingStatus.includes(status);

  const stealStream = () => {
    if (debug) console.log('Steal streaming');
    endOtherStream();
  };

  return (
    <>
      {isWaiting && <Spinner />}
      {status === 'error_already_streaming' ? (
        <>
          <Text variant="md">Already streaming</Text>
          <Button onClick={stealStream}>Steal Video Streaming</Button>
        </>
      ) : (
        status !== 'connected' && <Text variant="md">{statusText}</Text>
      )}
      <VideoFrame visible={status == 'connected'}>
        <video width="100%" ref={videoRef}>
          <Text variant="md">Your Browser does not support embedded videos.</Text>
        </video>
      </VideoFrame>
    </>
  );
};

const Content = styled.div({
  padding: '2.5rem',
  display: 'flex' /* Make parent a flex-container */,
  flexDirection: 'column',
  justifyContent: 'center' /* Centers child horizontally */,
  alignItems: 'center' /* Centers child vertically */,
});

const VideoFrame = styled.div({}, ({ visible }: { visible: boolean }) => ({
  background: visible ? 'gray' : null,
}));
